import React, { useEffect, useState } from 'react';

const CaseNotes = ({ caseId }) => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    category: 'general',
    color: 'default',
    is_pinned: false,
    is_confidential: false
  });
  const [submitting, setSubmitting] = useState(false);
  const [filterCategory, setFilterCategory] = useState('all');

  const categories = [
    { value: 'general', label: 'General', icon: '📝' },
    { value: 'strategy', label: 'Strategy', icon: '🎯' },
    { value: 'discovery', label: 'Discovery', icon: '🔍' },
    { value: 'client_communication', label: 'Client Communication', icon: '💬' },
    { value: 'court', label: 'Court', icon: '⚖️' }
  ];

  const colors = [
    { value: 'default', label: 'Default', bg: 'bg-virgo-navy-700/50', border: 'border-virgo-teal-700/30' },
    { value: 'yellow', label: 'Yellow', bg: 'bg-yellow-500/10', border: 'border-yellow-500/30' },
    { value: 'green', label: 'Green', bg: 'bg-green-500/10', border: 'border-green-500/30' },
    { value: 'blue', label: 'Blue', bg: 'bg-blue-500/10', border: 'border-blue-500/30' },
    { value: 'red', label: 'Red', bg: 'bg-red-500/10', border: 'border-red-500/30' },
    { value: 'purple', label: 'Purple', bg: 'bg-cyan-500/10', border: 'border-cyan-500/30' }
  ];

  useEffect(() => {
    loadNotes();
  }, [caseId, filterCategory]);

  const loadNotes = async () => {
    try {
      setLoading(true);
      const url = filterCategory === 'all'
        ? `/api/cases/${caseId}/notes`
        : `/api/cases/${caseId}/notes?category=${filterCategory}`;
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setNotes(data);
      }
    } catch (err) {
      console.error('Failed to load notes:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      const url = editingNote
        ? `/api/cases/${caseId}/notes/${editingNote.id}`
        : `/api/cases/${caseId}/notes`;

      const response = await fetch(url, {
        method: editingNote ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        resetForm();
        loadNotes();
      } else {
        throw new Error('Failed to save note');
      }
    } catch (err) {
      alert('Failed to save note');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (noteId) => {
    if (!window.confirm('Delete this note?')) return;

    try {
      const response = await fetch(`/api/cases/${caseId}/notes/${noteId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        loadNotes();
      }
    } catch (err) {
      alert('Failed to delete note');
    }
  };

  const handleTogglePin = async (note) => {
    try {
      await fetch(`/api/cases/${caseId}/notes/${note.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_pinned: !note.is_pinned })
      });
      loadNotes();
    } catch (err) {
      console.error('Failed to toggle pin:', err);
    }
  };

  const handleEdit = (note) => {
    setEditingNote(note);
    setFormData({
      title: note.title || '',
      content: note.content,
      category: note.category,
      color: note.color,
      is_pinned: note.is_pinned,
      is_confidential: note.is_confidential
    });
    setShowAddForm(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      category: 'general',
      color: 'default',
      is_pinned: false,
      is_confidential: false
    });
    setEditingNote(null);
    setShowAddForm(false);
  };

  const getColorClasses = (color) => {
    const colorDef = colors.find(c => c.value === color) || colors[0];
    return `${colorDef.bg} ${colorDef.border}`;
  };

  const getCategoryIcon = (category) => {
    const cat = categories.find(c => c.value === category);
    return cat ? cat.icon : '📝';
  };

  if (loading && notes.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-virgo-teal-400"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="flex items-center gap-4">
          <h3 className="text-xl font-bold text-white">Case Notes</h3>
          <span className="px-2 py-1 bg-cyan-500/20 text-cyan-300 text-sm rounded border border-cyan-500/30">
            {notes.length} notes
          </span>
        </div>

        <div className="flex items-center gap-3">
          {/* Category Filter */}
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white text-sm focus:ring-2 focus:ring-cyan-500"
          >
            <option value="all">All Categories</option>
            {categories.map(cat => (
              <option key={cat.value} value={cat.value}>{cat.icon} {cat.label}</option>
            ))}
          </select>

          {!showAddForm && (
            <button
              onClick={() => setShowAddForm(true)}
              className="px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg hover:from-cyan-600 hover:to-blue-600 font-medium shadow-lg shadow-cyan-500/30 transition-all"
            >
              + Add Note
            </button>
          )}
        </div>
      </div>

      {/* Add/Edit Form */}
      {showAddForm && (
        <div className="bg-virgo-navy-800/50 border border-virgo-teal-700/30 rounded-xl p-4 mb-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-virgo-teal-400 mb-1">Title (optional)</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white placeholder-virgo-teal-500 focus:ring-2 focus:ring-cyan-500"
                  placeholder="Note title..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-virgo-teal-400 mb-1">Category</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white focus:ring-2 focus:ring-cyan-500"
                >
                  {categories.map(cat => (
                    <option key={cat.value} value={cat.value}>{cat.icon} {cat.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-virgo-teal-400 mb-1">Content *</label>
              <textarea
                required
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                rows={5}
                className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white placeholder-virgo-teal-500 focus:ring-2 focus:ring-cyan-500"
                placeholder="Write your note..."
              />
            </div>

            <div className="flex flex-wrap items-center gap-4">
              <div>
                <label className="block text-sm font-medium text-virgo-teal-400 mb-1">Color</label>
                <div className="flex gap-2">
                  {colors.map(color => (
                    <button
                      key={color.value}
                      type="button"
                      onClick={() => setFormData({ ...formData, color: color.value })}
                      className={`w-6 h-6 rounded border-2 ${color.bg} ${
                        formData.color === color.value ? 'ring-2 ring-cyan-500' : ''
                      } ${color.value === 'default' ? 'border-virgo-teal-500' : `border-${color.value}-500`}`}
                      title={color.label}
                    />
                  ))}
                </div>
              </div>

              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_pinned}
                  onChange={(e) => setFormData({ ...formData, is_pinned: e.target.checked })}
                  className="h-4 w-4 text-cyan-600 focus:ring-cyan-500 border-virgo-teal-700 rounded bg-virgo-navy-600"
                />
                <span className="text-sm text-virgo-teal-300">Pin to top</span>
              </label>

              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_confidential}
                  onChange={(e) => setFormData({ ...formData, is_confidential: e.target.checked })}
                  className="h-4 w-4 text-cyan-600 focus:ring-cyan-500 border-virgo-teal-700 rounded bg-virgo-navy-600"
                />
                <span className="text-sm text-virgo-teal-300">Confidential</span>
              </label>
            </div>

            <div className="flex justify-end space-x-2">
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2 text-sm font-medium text-virgo-teal-300 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg hover:bg-virgo-navy-500 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-cyan-500 to-blue-500 rounded-lg hover:from-cyan-600 hover:to-blue-600 disabled:opacity-50 transition-all"
              >
                {submitting ? 'Saving...' : editingNote ? 'Update Note' : 'Add Note'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Notes List */}
      {notes.length === 0 ? (
        <div className="text-center py-12 text-virgo-teal-400">
          No notes yet. Add your first note above.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {notes.map((note) => (
            <div
              key={note.id}
              className={`relative border rounded-xl p-4 hover:border-cyan-500/30 transition-all ${getColorClasses(note.color)}`}
            >
              {/* Pin indicator */}
              {note.is_pinned && (
                <span className="absolute top-2 right-2 text-lg" title="Pinned">📌</span>
              )}

              {/* Confidential indicator */}
              {note.is_confidential && (
                <span className="absolute top-2 right-8 text-sm bg-red-500/20 text-red-300 px-2 py-0.5 rounded border border-red-500/30">
                  Confidential
                </span>
              )}

              {/* Category badge */}
              <div className="flex items-center gap-2 mb-2">
                <span className="text-lg">{getCategoryIcon(note.category)}</span>
                <span className="text-xs text-virgo-teal-400 uppercase">{note.category.replace('_', ' ')}</span>
              </div>

              {/* Title */}
              {note.title && (
                <h4 className="font-semibold text-white mb-2">{note.title}</h4>
              )}

              {/* Content */}
              <p className="text-sm text-virgo-teal-200 whitespace-pre-wrap mb-3">{note.content}</p>

              {/* Footer */}
              <div className="flex items-center justify-between text-xs text-virgo-teal-500">
                <span>
                  {new Date(note.created_at).toLocaleDateString()} by {note.created_by}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleTogglePin(note)}
                    className="hover:text-cyan-400 transition-colors"
                    title={note.is_pinned ? 'Unpin' : 'Pin'}
                  >
                    {note.is_pinned ? '📌' : '📍'}
                  </button>
                  <button
                    onClick={() => handleEdit(note)}
                    className="hover:text-cyan-400 transition-colors"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(note.id)}
                    className="hover:text-red-400 transition-colors"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CaseNotes;
